import React from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, Loader2 } from "lucide-react";
import { SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { GetDateInMMDDYYYY } from "../../utils/formatters/GetDateInMMDDYYYY";

const NotificationSheetContent = ({
  notifications,
  onMarkAsRead,
  onMarkAllAsRead,
  onClose,
}) => {
  const [selectedNotif, setSelectedNotif] = React.useState(null);
  const [isMarkingAllRead, setIsMarkingAllRead] = React.useState(false);

  // Sort newest first by CreatedAt
  const sortedNotifications = React.useMemo(
    () =>
      [...notifications].sort(
        (a, b) => new Date(b.CreatedAt) - new Date(a.CreatedAt),
      ),
    [notifications],
  );

  // Keep selectedNotif in sync when the parent updates notifications (e.g. after mark-as-read)
  React.useEffect(() => {
    if (selectedNotif) {
      const updated = notifications.find((n) => n.pk === selectedNotif.pk);
      if (updated) setSelectedNotif(updated);
    }
  }, [notifications]);

  const unreadCount = notifications.filter((n) => !n.isRead).length;

  const handleMarkAllAsRead = async () => {
    setIsMarkingAllRead(true);
    try {
      await onMarkAllAsRead();
    } finally {
      setIsMarkingAllRead(false);
    }
  };

  const handleItemClick = (notif) => {
    setSelectedNotif(notif);
    if (!notif.isRead) {
      onMarkAsRead(notif.pk);
    }
  };

  return (
    <>
      {/* Header */}
      <SheetHeader className="px-4 py-3 border-b border-gray-200 dark:border-slate-700 flex-shrink-0 space-y-0">
        <div className="flex items-center justify-between pr-8">
          <div className="flex items-center gap-2">
            {selectedNotif && (
              <button
                data-cy="backToNotifications"
                onClick={() => setSelectedNotif(null)}
                className="p-1 rounded hover:bg-gray-100 dark:hover:bg-slate-700 text-gray-600 dark:text-gray-400"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
            )}
            <SheetTitle className="text-sm font-semibold text-gray-900 dark:text-white">
              {selectedNotif
                ? selectedNotif.heading || "Notification"
                : "Notifications"}
            </SheetTitle>
          </div>
          {!selectedNotif && (
            <div className="flex items-center gap-2">
              {unreadCount > 0 && (
                <button
                  data-cy="markAllAsRead"
                  onClick={handleMarkAllAsRead}
                  disabled={isMarkingAllRead}
                  className="text-xs px-2 py-1 rounded border border-blue-300 dark:border-blue-600 text-blue-600 dark:text-blue-400 hover:bg-blue-50 hover:text-white dark:hover:bg-blue-900/30 font-medium whitespace-nowrap transition-colors flex items-center gap-1 disabled:opacity-70 disabled:cursor-not-allowed"
                >
                  {isMarkingAllRead ? (
                    <>
                      <Loader2 className="w-3 h-3 animate-spin" />
                      Marking...
                    </>
                  ) : (
                    "Mark all as read"
                  )}
                </button>
              )}
              <span className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
                {unreadCount} unread
              </span>
            </div>
          )}
        </div>
      </SheetHeader>

      {/* Scrollable content */}
      <div className="flex-1 overflow-y-auto">
        {selectedNotif ? (
          <div className="p-5">
            <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">
              {selectedNotif.notificationType || "Notification"}
            </p>
            <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-3 leading-snug">
              {selectedNotif.heading}
            </h4>
            <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap leading-relaxed mb-4">
              {selectedNotif.message}
            </p>
            <p className="text-xs text-gray-400 dark:text-gray-500">
              {GetDateInMMDDYYYY(selectedNotif.CreatedAt)}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100 dark:divide-slate-700">
            {notifications.length === 0 ? (
              <div className="p-8 text-center text-gray-500 dark:text-gray-400 text-sm">
                No notifications yet
              </div>
            ) : (
              sortedNotifications.map((notif) => (
                <button
                  key={notif.pk}
                  data-cy={`notificationItem`}
                  onClick={() => handleItemClick(notif)}
                  disabled={isMarkingAllRead && !notif.isRead}
                  className={`relative w-full text-left px-4 py-3 flex items-start gap-3 hover:bg-gray-100 dark:hover:bg-slate-700/60 transition-colors ${
                    !notif.isRead ? "bg-gray-50 dark:bg-slate-700/30" : ""
                  } ${isMarkingAllRead && !notif.isRead ? "opacity-60" : ""}`}
                >
                  {isMarkingAllRead && !notif.isRead && (
                    <span className="absolute inset-0 flex items-center justify-end pr-4 pointer-events-none">
                      <Loader2 className="w-3.5 h-3.5 animate-spin text-blue-400 dark:text-blue-500" />
                    </span>
                  )}
                  <span
                    className={`mt-1.5 w-2 h-2 rounded-full flex-shrink-0 ${
                      notif.isRead
                        ? "bg-gray-300 dark:bg-slate-600"
                        : "bg-blue-500"
                    }`}
                  />
                  <div className="flex-1 min-w-0">
                    <p
                      className={`text-sm truncate ${
                        !notif.isRead
                          ? "font-semibold text-gray-900 dark:text-white"
                          : "font-medium text-gray-700 dark:text-gray-300"
                      }`}
                    >
                      {notif.heading}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 line-clamp-2">
                      {notif.message}
                    </p>
                    <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                      {GetDateInMMDDYYYY(notif.CreatedAt)}
                    </p>
                  </div>
                </button>
              ))
            )}
          </div>
        )}
      </div>

      {/* Footer */}
      {!selectedNotif && (
        <div className="border-t border-gray-200 dark:border-slate-700 flex-shrink-0">
          <Link
            to="/notifications"
            onClick={onClose}
            className="block py-2.5 text-center text-sm text-blue-600 dark:text-blue-400 hover:bg-gray-50 dark:hover:bg-slate-700/60 transition-colors font-medium"
          >
            View all notifications
          </Link>
        </div>
      )}
    </>
  );
};

export default NotificationSheetContent;
